// src/components/Inquiry/utils/inquiryPayload.ts
import dayjs from "dayjs";
import { CartType } from "types/CartTypes";
import { OrderType } from "types/OrderTypes";
import { CreateInquiryItemType, RenderInquiryType } from "types/InquiryTypes";
import { InquiryFormValues } from "../types";

/**
 * The deadline a new inquiry starts on: the day before the order or quote it
 * is raised from has to ship. Nothing to derive when it is raised from
 * neither, so a standalone inquiry starts with the field empty.
 */
export function getInitialDeadline(
  prodObjIn: OrderType | CartType | undefined,
  prodType: "order" | "cart"
): string | undefined {
  if (!prodObjIn) return undefined;

  const tempDate =
    prodType === "order"
      ? (prodObjIn as OrderType).shipmentDate
      : (prodObjIn as CartType).shipmentDate;

  let date = dayjs(tempDate.split("T")[0]);
  date = date.subtract(1, "day");
  return date.format("YYYY-MM-DD");
}

function mapItem(item: CreateInquiryItemType): CreateInquiryItemType {
  return {
    productId: item.productId,
    orderItemId: item.orderItemId ?? null,
    quoteItemId: item.quoteItemId ?? null,
    quantity: item.quantity,
  };
}

/**
 * Builds the API payload for both create and render (preview) calls.
 * Replaces the old createReqCommand + renderReqCommand duplication.
 * Pass `id` when building a render payload for an existing inquiry.
 */
export function buildInquiryPayload(
  formValues: InquiryFormValues,
  mergedItems: CreateInquiryItemType[],
  prodObj: OrderType | CartType | undefined,
  prodType: "order" | "cart",
  id?: number
): RenderInquiryType {
  const items = mergedItems.map(mapItem);
  // An emptied date input reads as "", which the backend would store as a
  // falsy delivery_date rather than leave unset.
  const deadline = formValues.deadline || undefined;
  // EE-240: partner history, sent on both branches below
  const history = {
    deliveryTime: formValues.deliveryTime,
    notes: formValues.notes,
  };

  if (prodObj) {
    const payload: RenderInquiryType = {
      id,
      partnerId: formValues.partnerId!,
      type: formValues.type,
      // create_inquiry assigns the title whether or not an order is linked, so
      // it is sent on both branches. It used to be sent only on the standalone
      // one, which is why an order-linked inquiry always had a null title.
      title: formValues.title,
      deadline,
      deliveryMethod: formValues.deliveryMethod,
      message: formValues.message,
      items,
      ...history,
    };
    if (prodType === "order") {
      payload.orderId = prodObj.id;
    } else {
      payload.quoteId = prodObj.id;
    }
    return payload;
  }

  return {
    id,
    partnerId: formValues.partnerId!,
    title: formValues.title,
    deadline,
    deliveryMethod: formValues.deliveryMethod,
    message: formValues.message,
    items,
    // Was hardcoded to "CUSTOM", which silently discarded the Type selector on
    // an inquiry raised from nothing.
    type: formValues.type,
    ...history,
  };
}
